import React from 'react';
import TrafficLight from './TrafficLight';
import {
  activationStatusTitle,
  getEffectiveActivationStatus,
} from './parseActivityRows';

const BADGE_CLASS = {
  active: 'bg-success',
  activating: 'bg-warning text-dark',
  handing_over: 'bg-warning text-dark',
  queued: 'bg-warning text-dark',
  error: 'bg-danger',
  inactive: 'bg-secondary',
};

const LABEL = {
  active: 'Active',
  activating: 'Activating',
  handing_over: 'Switching',
  queued: 'Queued',
  error: 'Error',
  inactive: 'Inactive',
};

/**
 * Canonical display for port **activation** status (hardware + queue fail-safe).
 * Distinct from SIM lifecycle (unused / expiring / dead) in portHelpers.js.
 */
export default function PortActivationBadge({
  statusEntry = {},
  countdown = null,
  compact = false,
  showTrafficLight = true,
  className = '',
}) {
  const effectiveStatus = getEffectiveActivationStatus(statusEntry);
  const show30sWarning = !!statusEntry.show_30s_warning;
  const messageTriggered = !!statusEntry.message_triggered;
  const isStale = !!statusEntry.stale;

  const resolvedCountdown =
    countdown != null ? countdown : statusEntry.remaining_seconds ?? null;

  let badgeClass = BADGE_CLASS[effectiveStatus] || BADGE_CLASS.inactive;
  if (effectiveStatus === 'active' && show30sWarning) {
    badgeClass = 'bg-warning text-dark';
  }

  const label = LABEL[effectiveStatus] || effectiveStatus;
  const title = activationStatusTitle(effectiveStatus, statusEntry);

  return (
    <span className={`d-inline-flex align-items-center flex-wrap gap-1 ${className}`}>
      {showTrafficLight && !compact && (
        <TrafficLight
          status={effectiveStatus}
          animate={effectiveStatus === 'activating'}
        />
      )}
      <span className={`badge ${badgeClass}`} title={title}>
        {effectiveStatus === 'active' && show30sWarning ? '⚠️ ' : ''}
        {compact && effectiveStatus === 'active' && !show30sWarning ? '🟢 ' : ''}
        {compact && effectiveStatus === 'inactive' ? '⚪ ' : ''}
        {label}
      </span>
      {resolvedCountdown != null &&
        resolvedCountdown > 0 &&
        (effectiveStatus === 'queued' || effectiveStatus === 'active') && (
          <span className="badge bg-light text-dark" title="Time remaining">
            {resolvedCountdown}s
          </span>
        )}
      {messageTriggered && (
        <span className="badge bg-info text-dark" title="Activated by incoming message">
          ⚡
        </span>
      )}
      {isStale && effectiveStatus !== 'activating' && (
        <span
          className="badge bg-warning text-dark"
          title={`Hardware cache may be outdated${
            typeof statusEntry.cache_age_seconds === 'number'
              ? ` (${statusEntry.cache_age_seconds}s)`
              : ''
          }`}
        >
          stale
        </span>
      )}
    </span>
  );
}
